import React, { useState, useEffect, useContext } from "react";
import {
AppBar,
Typography,
Button,
IconButton,
TextField,
Box,
Breadcrumbs,
Autocomplete,
InputAdornment,
Link,
Dialog,
DialogTitle,
DialogContent,
DialogContentText,
DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { externalgetApi, externaldeleteApi } from "../../services/externalApis.tsx";
import { SETTING_API_02, SETTING_API_20 } from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
Search as SearchIcon,
Delete as DeleteIcon,
} from "@mui/icons-material";
import { PageDataContext } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import { Catergorydata } from "../../components/grid/GridData.tsx";

// Types and Interfaces
interface MainCategory {
maincatcode: string | number;
maincatname: string;
}

interface Category {
catcode: string | number;
catname: string;
maincatcode?: string | number;
descrip?: string;
[key: string]: unknown;
}

interface UserParams {
LOCCODE?: string;
STAFFMAPLOCODE?: string;
COMPCODE?: string;
USERNAME?: string;
}

interface UserState {
USER_PARAM?: UserParams;
}

interface MenuItem {
text: string;
icon: React.ReactElement;
onClick: (id: string | number) => void;
}

interface ApiError {
response?: {
data?: {
message?: string;
};
};
message?: string;
}

interface DeletePayload {
COMPCODE?: string;
LOCCODE?: string;
catcode: string | number;
maincatcode: string | number;
catname: string;
descrip: string;
sts: string;
enuser: string;
MODE: string;
}

interface ApiResponse {
success?: boolean;
message?: string;
}

interface AutocompleteOption {
label: string;
value: string | number;
}

// Interface for rows with id
interface RowWithId extends Category {
id: string | number;
}

function ViewCategory(): React.ReactElement {
const theme = useTheme();
const styles = useStyles(theme);
const navigate = useNavigate();
const { setPageData } = useContext(PageDataContext);

const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
SYS_USER.USER_PARAM || {};

const [isLoading, setIsLoading] = useState<boolean>(false);
const [data, setData] = useState<Category[]>([]);
const [filteredRows, setFilteredRows] = useState<Category[]>([]);
const [searchQuery, setSearchQuery] = useState<string>("");
const [alertOpen, setAlertOpen] = useState<boolean>(false);
const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
const [alertMessage, setAlertMessage] = useState<string>("");
const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
const [selectedId, setSelectedId] = useState<string | number | null>(null);
const [selectedItem, setSelectedItem] = useState<Category | null>(null);

const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
const [selectedMainCategory, setSelectedMainCategory] = useState<string | number>("");

// Menu items
const menuItems: MenuItem[] = [
{
text: "Edit",
icon: <BorderColorOutlinedIcon />,
onClick: (id: string | number) => handleItemClick(id, "Edit"),
},
{
text: "Delete",
icon: <DeleteIcon />,
onClick: (id: string | number) => handleItemClick(id, "Delete"),
},
];

// Fetch categories for selected main category
const fetchCategories = async (): Promise<void> => {
try {
setIsLoading(true);
const result = await externalgetApi(SETTING_API_02, "get", {
COMPCODE,
LOCCODE: STAFFMAPLOCODE,
MAINCATCODE: selectedMainCategory,
STS: CON_STATUS_ACTIVE,
});
const list = Array.isArray(result) ? result : (result as any)?.data || [];
setData(list);
setFilteredRows(list);
} catch (err) {
console.error(err);
setAlertMessage("Failed to fetch categories.");
setAlertType("error");
setAlertOpen(true);
} finally {
setIsLoading(false);
}
};

// Fetch main categories
const fetchMainCategories = async (): Promise<void> => {
try {
setIsLoading(true);
const params = {
COMPCODE,
LOCCODE: STAFFMAPLOCODE,
STS: CON_STATUS_ACTIVE,
};

const result = await externalgetApi(SETTING_API_20, "get", params);
const list = Array.isArray(result) ? result : (result as any)?.data || [];
setMainCategories(list);

if (list.length > 0) {
setSelectedMainCategory(list[0].maincatcode);
}
} catch (err) {
console.error("Failed to fetch main categories:", err);
setAlertMessage("Failed to fetch main categories.");
setAlertType("error");
setAlertOpen(true);
} finally {
setIsLoading(false);
}
};

// Handlers
// Search handler
const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
  const searchValue = value || "";
  setSearchQuery(searchValue);
  
  if (!searchValue.trim()) {
    setFilteredRows(data || []);
    return;
  }

  const q = searchValue.toLowerCase().trim();
  
  const filtered = data?.filter((row) => {
    // Convert catcode to string for comparison if it's a number
    const catcodeStr = row.catcode?.toString() || "";
    
    return (
      row.catname?.toLowerCase().includes(q) ||
      catcodeStr.toLowerCase().includes(q)
    );
  });
  
  setFilteredRows(filtered || []);
};

// Menu item click handler
const handleItemClick = (id: string | number, actionText: string): void => {
const item = data.find((row) => row.catcode === id);
if (!item) return;

if (actionText === "Edit") {
navigate("/createCategory", {
state: {
selectedItem: item,
actionText,
selectedMainCategory,
existingCategories: data,
maincatcode: item.maincatcode || "",
},
});
}

if (actionText === "Delete") {
setSelectedId(id);
setSelectedItem(item);
setOpenDeleteDialog(true);
}
};

// Confirm delete handler
const confirmDelete = async (): Promise<void> => {
try {
setIsLoading(true);

if (!selectedId || !selectedItem) {
throw new Error("No category selected");
}

const deletePayload: DeletePayload = {
COMPCODE,
LOCCODE: STAFFMAPLOCODE,
catcode: selectedId,
maincatcode: selectedItem.maincatcode || "",
catname: selectedItem.catname || "",
descrip: selectedItem.descrip || "",
sts: "D",
enuser: USERNAME || "Admin",
MODE: "DELETE",
};

const response = (await externaldeleteApi(
SETTING_API_02,
"DELETE",
"",
deletePayload
)) as ApiResponse;

if (response && response.success !== false) {
setData((prev) => prev.filter((row) => row.catcode !== selectedId));
setFilteredRows((prev) =>
prev.filter((row) => row.catcode !== selectedId)
);

setAlertMessage(
`Category "${selectedItem.catname}" deleted successfully`
);
setAlertType("success");
setAlertOpen(true);
} else {
throw new Error(response?.message || "Delete operation failed");
}
} catch (error) {
const err = error as ApiError;
console.error("DELETE API error:", err);
const errorMessage =
err?.response?.data?.message ||
(err?.response?.data as string) ||
err?.message ||
"Failed to delete category.";

setAlertMessage(`Category cannot be deleted. It's already in use`);
// setAlertMessage(errorMessage);
setAlertType("error");
setAlertOpen(true);
} finally {
setIsLoading(false);
setOpenDeleteDialog(false);
setSelectedId(null);
setSelectedItem(null);
}
};

// Cancel delete handler
const cancelDelete = (): void => {
setOpenDeleteDialog(false);
setSelectedId(null);
setSelectedItem(null);
};

// Add button
const addToView = (): void => {
navigate("/createCategory", {
state: { selectedMainCategory, existingCategories: data },
});
};

// Helper functions for Autocomplete options
const getMainCategoryOptions = (): AutocompleteOption[] => {
return mainCategories.map((mc) => ({
label: mc.maincatname || "",
value: mc.maincatcode,
}));
};

const findSelectedMainCategory = (): AutocompleteOption | undefined => {
const options = getMainCategoryOptions();
return options.find((opt) => opt.value === selectedMainCategory);
};

// Prepare rows with id for the grid
const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
...row,
id: row.catcode || Math.random(),
}));

// Placeholder processRowUpdate function
const processRowUpdate = async (newRow: any): Promise<any> => {
console.log("Row update attempted:", newRow);
// Return the new row or the updated row
return newRow;
};

// useEffect
useEffect(() => {
fetchMainCategories();
}, [COMPCODE, STAFFMAPLOCODE]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
if (!selectedMainCategory) return;
fetchCategories();
}, [COMPCODE, STAFFMAPLOCODE, selectedMainCategory]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
setPageData({ title: "Category" });
}, [setPageData]);

return (
<>
<Box sx={styles.box_breadcrumb_view}>
<Breadcrumbs
separator="›"
aria-label="breadcrumb"
sx={styles.breadcrumb}
>
<Link underline="hover" sx={styles.breadcrumb_link}>
Home
</Link>
<Link underline="hover" sx={styles.breadcrumb_link}>
Settings
</Link>
<Link
underline="hover"
sx={styles.breadcrumb_link_page}
aria-current="page"
>
Category
</Link>
</Breadcrumbs>
</Box>

<Box sx={{ ...styles.box_page_container }}>
<AppBar sx={styles.appbar}>
<Grid
container
spacing={2}
sx={{
...styles.appbar_grid_container,
alignItems: "center",
justifyContent: "space-between",
}}
>
{/* Left: Title */}
<Grid size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
<Typography sx={styles.appbar_title}>
{Catergorydata.name}
</Typography>
</Grid>

{/* Right: Main Category , Search , Add */}
<Grid size={{ xs: 12, sm: 8, md: 8, lg: 8, xl: 8 }} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
{/* Main Category Dropdown */}
{/* Main Category Dropdown */}
<Autocomplete
  id="act_maincategory"
  options={getMainCategoryOptions()}
  getOptionLabel={(option: AutocompleteOption) => option.label || ""}
  isOptionEqualToValue={(option, value) =>
    option.value === value.value
  }
  value={findSelectedMainCategory() || null}
  onChange={(_, newValue: AutocompleteOption | null) => {
    setSelectedMainCategory(newValue ? newValue.value : "");
  }}
  sx={{ minWidth: 200 }}
  disableClearable  // Add this prop to remove the cross/clear button
  renderInput={(params) => (
    <TextField
      {...params}
      label="Main Category"
      size="small"
      fullWidth
    />
  )}
/>
<Grid
size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
sx={styles.appbar_grid_search}
>
{/* Search Autocomplete */}
<Autocomplete
freeSolo
id="act_appbarsearch"
fullWidth
disableClearable
value={searchQuery}
onInputChange={handleSearchChange}
sx={{ ...styles.ipt_appbar_search_auto, minWidth: 200 }}
options={data?.map((row) => row.catname) || []}
renderInput={(params) => {
const { id, ...restParams } = params;
return (
<TextField
id="txt_appbarsearch"
{...restParams}
placeholder="Search by..."
size="small"
sx={styles.ipt_appbar_search}
InputProps={{
...restParams.InputProps,
type: "search",
endAdornment: (
<InputAdornment position="end">
<IconButton
id="btn_search"
sx={styles.btn_appbar_search_icon}
>
<SearchIcon id="ico_search" />
</IconButton>
</InputAdornment>
),
}}
/>
);
}}
/>
</Grid>

{/* Add Button */}
<Button
id="btn_add"
variant="contained"
startIcon={<AddCircleOutlineIcon />}
size="small"
sx={{ height: 40, textTransform: "none" }}
onClick={addToView}
>
Add
</Button>
</Grid>
</Grid>
</AppBar>

<Box sx={styles.view_page_container}>
<ViewDataGrid
columns={Catergorydata.columns}
rows={rowsWithId}
menuItems={menuItems}
tabletitle={Catergorydata.name}
gridid="catcode"
processRowUpdate={processRowUpdate}
/>
</Box>
</Box>

<SystemAlert
open={alertOpen}
type={alertType}
message={alertMessage}
onClose={() => setAlertOpen(false)}
/>

<Dialog
open={openDeleteDialog}
onClose={cancelDelete}
aria-labelledby="delete-dialog-title"
>
<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
<DialogContent>
<DialogContentText>
Are you sure you want to delete this category?
</DialogContentText>
</DialogContent>
<DialogActions>
<Button
id="btn_cancel"
onClick={cancelDelete}
variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
>
Cancel
</Button>
<Button
id="btn_delete"
onClick={confirmDelete}
color="error"
sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
variant="contained"
>
Delete
</Button>
</DialogActions>
</Dialog>
</>
);
}

export default ViewCategory;